import { toast } from "sonner";
import { Link } from "react-router-dom";
import { AlertTriangle, GitCompare, Pencil } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { STATES, itemName } from "@/lib/imports";
import { layoutLabel } from "@/lib/mtg";
import { useDraft } from "@/components/import/DraftContext";
import { FaceSlot } from "@/components/import/FaceSlot";

function ActionPicker({ item, onDiff }) {
  const { id, refresh, open } = useDraft();
  const set = async (action) => {
    try {
      await api.patch(`/imports/${id}/items/${item.item_id}`, { action });
      refresh();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };
  return (
    <div className="flex flex-wrap items-center gap-2 rounded border border-copper-700/40 bg-copper-900/20 px-3 py-2 text-xs" data-testid={`import-existing-${item.item_id}`}>
      <span className="text-copper-200">Matches your card <Link to={`/cards/${item.existing.card_id}`} className="underline">{item.existing.name}</Link> (by {item.existing.by.replace(/_/g, " ")})</span>
      <select className="field w-auto py-1 text-xs" value={item.action} disabled={!open || item.status !== "pending"} onChange={(e) => set(e.target.value)} data-testid={`import-action-${item.item_id}`}>
        <option value="skip">Skip it</option>
        <option value="update" disabled={item.existing.uploaded_design}>Update existing</option>
        <option value="create">Create new card</option>
      </select>
      <button className="btn-ghost px-2 py-1 text-xs" onClick={() => onDiff(item)} data-testid={`import-diff-${item.item_id}`}><GitCompare className="h-3.5 w-3.5" />Preview changes</button>
    </div>
  );
}

function Problems({ item }) {
  if (!item.errors.length && !item.warnings.length && !item.result_error) return null;
  return (
    <ul className="space-y-1 text-xs" data-testid={`import-problems-${item.item_id}`}>
      {item.result_error && <li className="text-red-300">Could not save: {item.result_error}</li>}
      {item.errors.map((e, i) => <li key={`e${i}`} className="text-red-300"><span className="font-mono">{e.field}</span> {e.message}</li>)}
      {item.warnings.slice(0, 4).map((w, i) => <li key={`w${i}`} className="flex gap-1 text-amber-300/80"><AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />{w}</li>)}
    </ul>
  );
}

function ReadAs({ item }) {
  const c = item.card || {};
  const f = c.faces?.[0] || {};
  const t = item.read_as?.[0] || {};
  const rows = [["Supertypes", t.supertypes?.join(", ")], ["Types", t.types?.join(", ")], ["Subtypes", t.subtypes?.join(", ")],
    ["Cost", f.mana_cost], ["P/T", f.power ? `${f.power}/${f.toughness}` : null], ["Loyalty", f.loyalty], ["Defense", f.defense], ["Rarity", c.rarity],
    ["Artist", f.artist], ["Mechanics", c.mechanics?.join(", ")]];
  return (
    <dl className="flex flex-wrap gap-x-4 gap-y-1 text-[11px]" data-testid={`import-read-as-${item.item_id}`}>
      {rows.map(([k, v]) => <div key={k} className="flex gap-1"><dt className="text-slate-500">{k}</dt><dd className="text-slate-300">{v || "—"}</dd></div>)}
    </dl>
  );
}

export function ItemRow({ item, onEdit, onDiff }) {
  const { open } = useDraft();
  const st = STATES[item.state] || STATES.ready;
  return (
    <li className="flex flex-col gap-4 py-4 sm:flex-row" data-testid={`import-item-${item.item_id}`}>
      <div className="flex gap-2">{item.slots.map((s) => <FaceSlot key={s.face} item={item} slot={s} />)}</div>
      <div className="min-w-0 flex-1 space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <p className="font-heading text-base text-amber-100" data-testid={`import-item-name-${item.item_id}`}>{itemName(item)}</p>
          <span className={`rounded border px-2 py-0.5 font-mono text-[10px] uppercase ${st.cls}`} data-testid={`import-item-state-${item.item_id}`}>{st.label}</span>
        </div>
        <p className="font-mono text-xs text-slate-500">
          {item.import_id} · {item.card?.set_code || "no set"} {item.card?.collector_number ? `#${item.card.collector_number}` : ""} · {layoutLabel(item.card?.layout || "normal")} · {item.faces_required} image{item.faces_required > 1 ? "s" : ""}
        </p>
        {item.card && <ReadAs item={item} />}
        {item.card?.faces?.[0]?.oracle_text && <p className="line-clamp-2 whitespace-pre-line text-xs text-slate-400">{item.card.faces[0].oracle_text}</p>}
        <Problems item={item} />
        {item.existing && <ActionPicker item={item} onDiff={onDiff} />}
        {item.card_id && <Link to={`/cards/${item.card_id}`} className="text-xs text-aether-300 hover:underline" data-testid={`import-item-card-link-${item.item_id}`}>View imported card</Link>}
      </div>
      {open && item.status === "pending" && (
        <button className="btn-ghost self-start px-2 py-1 text-xs" onClick={() => onEdit(item)} data-testid={`import-edit-${item.item_id}`}><Pencil className="h-3.5 w-3.5" />Fix data</button>
      )}
    </li>
  );
}
